import { useEffect, useState } from "react";
import { MessageSquare, Menu, X, Clock } from "lucide-react";
import { PHONE, PHONE_TEL, PHONE_HELP, PHONE_HELP_TEL, BRAND } from "@/lib/api";

const NAV = [
  { label: "All Services", href: "#services" },
  { label: "Pre-Foreclosure", href: "#pre-foreclosure" },
  { label: "Surplus Funds", href: "#surplus-funds" },
  { label: "Sell Fast", href: "#sell-fast" },
  { label: "Partners", href: "#agents" },
  { label: "Cash Buyers", href: "#cash-buyers" },
  { label: "Wholesalers", href: "#wholesalers" },
  { label: "Veterans", href: "#veterans" },
  { label: "FAQ", href: "#faq" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-xl border-b border-[#E5E3DB] shadow-[0_1px_0_rgba(11,27,61,0.04)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-3 shrink-0" data-testid="brand-logo">
          <div className={`h-9 w-9 rounded-md flex items-center justify-center transition-colors shrink-0 ${scrolled ? 'bg-[#0B1B3D]' : 'bg-white/10 border border-white/25 backdrop-blur-sm'}`}>
            <span className="font-display text-[#C5A059] text-xl leading-none">C</span>
          </div>
          <div className="leading-tight">
            <div className={`font-display text-sm sm:text-base lg:text-lg transition-colors whitespace-nowrap ${scrolled ? 'text-[#0B1B3D]' : 'text-white'}`}>{BRAND}</div>
            <div className={`hidden sm:block text-[9px] tracking-[0.22em] uppercase transition-colors ${scrolled ? 'text-[#64748B]' : 'text-white/60'}`}>
              Nationwide Homeowner Advocates
            </div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-4 xl:gap-6 shrink-0 ml-6">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              className={`text-[13px] xl:text-sm whitespace-nowrap transition-colors ${scrolled ? 'text-[#0F172A] hover:text-[#0B1B3D]' : 'text-white/85 hover:text-[#C5A059]'}`}
              data-testid={`nav-${n.href.replace('#', '')}`}
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2 shrink-0">
          <a
            href={`tel:${PHONE_HELP_TEL}`}
            data-testid="header-24h-cta"
            className={`hidden lg:inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${scrolled ? 'bg-[#2C5545]/10 text-[#2C5545] hover:bg-[#2C5545]/15' : 'bg-white/10 text-white hover:bg-white/20 backdrop-blur-sm border border-white/25'}`}
            title="24/7 help line — call anytime"
          >
            <Clock className="h-3.5 w-3.5" />
            <span className="flex flex-col leading-tight text-left">
              <span className="text-[13px]">24/7 · {PHONE_HELP}</span>
              <span className={`text-[9px] uppercase tracking-[0.14em] ${scrolled ? 'text-[#2C5545]/70' : 'text-white/60'}`}>Call anytime</span>
            </span>
          </a>
          <a
            href={`sms:${PHONE_TEL}`}
            data-testid="header-phone-cta"
            className={`hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-all shadow-md hover:shadow-lg text-sm font-medium ${scrolled ? 'bg-[#0B1B3D] text-white hover:bg-[#1A2F5C]' : 'bg-[#C5A059] text-[#0B1B3D] hover:bg-[#B38F4E]'}`}
            title="Text us — business hours 8am–5pm ET"
          >
            <MessageSquare className="h-3.5 w-3.5" />
            <span className="flex flex-col leading-tight text-left">
              <span>Text {PHONE}</span>
              <span className={`text-[9px] uppercase tracking-[0.14em] ${scrolled ? 'text-white/70' : 'text-[#0B1B3D]/70'}`}>8am – 5pm</span>
            </span>
          </a>
          <button
            aria-label="Toggle menu"
            onClick={() => setOpen(!open)}
            className={`lg:hidden p-2 transition-colors ${scrolled ? 'text-[#0B1B3D]' : 'text-white'}`}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-[#E5E3DB]" data-testid="mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-3">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setOpen(false)}
                className="py-2 text-[#0F172A] text-sm"
              >
                {n.label}
              </a>
            ))}
            <a
              href={`sms:${PHONE_TEL}`}
              className="mt-2 inline-flex flex-col items-center justify-center gap-0.5 px-5 py-3 rounded-lg bg-[#0B1B3D] text-white text-sm font-medium"
            >
              <span className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                Text {PHONE}
              </span>
              <span className="text-[10px] uppercase tracking-[0.16em] text-white/70">8am – 5pm</span>
            </a>
            <a
              href={`tel:${PHONE_HELP_TEL}`}
              className="mt-1 inline-flex flex-col items-center justify-center gap-0.5 px-5 py-3 rounded-lg border border-[#0B1B3D]/30 text-[#0B1B3D] text-sm font-medium"
            >
              <span className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                24/7 Help: {PHONE_HELP}
              </span>
              <span className="text-[10px] uppercase tracking-[0.16em] text-[#64748B]">Call anytime</span>
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
